import { Button, Input, Popconfirm, Select, Space, Switch, Tooltip } from "antd";
import { TableProps } from "antd/es/table";
import { useEffect } from "react";
import { useAppSelector } from "../../../app/store";
import AddIcon from "../../../assets/icons/AddIcon";
import DeleteIcon from "../../../assets/icons/DeleteIcon";
import EditIcon from "../../../assets/icons/EditIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import CustomTable from "../../../components/ui/CustomTable";
import {
  useDeleteWorkStationMutation,
  useGetWorkStationsQuery,
  useUpdateWorkStationStatusMutation,
} from "../../../features/org-setting/workStation/workStationApi";
import useDebounce from "../../../hooks/useDebounce";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { toast } from "../../../lib/toast";
import {
  GetWorkStationsParams,
  WorkStationData,
} from "../../../types/org-setting/workStation";
import { countParPageSerialNumber } from "../../../utils/number";
import {
  addKeyInArray,
  selectSearchOption,
  skipData,
} from "../../../utils/utils";
import ManageWorkStation from "./_components/ManageWorkStation";

enum WorkStationStatus {
  "Inactive",
  "Active",
}

export default function WorkStation() {
  const { open, handleClose, handleOpen } = useModal();
  const { keyword, handleInputChange } = useDebounce();
  const { user } = useAppSelector((state) => state.auth);
  const {
    filter,
    page,
    limit,
    handlePagination,
    handleLimit,
    resetPagination,
    handleFilter,
  } = useTable<GetWorkStationsParams>();
  const [
    updateWorkStationStatus,
    {
      isSuccess: isStatusUpdateSuccess,
      isError: isStatusUpdateError,
      error: statusUpdateError,
      isLoading: isStatusUpdateLoading,
    },
  ] = useUpdateWorkStationStatusMutation();
  const [
    deleteWorkStation,
    {
      isSuccess: isDeleteSuccess,
      isError: isDeleteError,
      error: deleteError,
      isLoading: isDeleteLoading,
    },
  ] = useDeleteWorkStationMutation();
  const { isLoading, isError, error, data } = useGetWorkStationsQuery({
    params: {
      ...filter,
      limit,
      skip: skipData(page, limit),
      ...(keyword ? { search: keyword } : {}),
      company_id: user?.data?.company_id,
    },
  });

  useError(isError, error, "Get work station data fail");
  useError(
    isStatusUpdateError,
    statusUpdateError,
    "Work station status update fail"
  );
  useError(isDeleteError, deleteError, "Work station delete fail");

  const columns: TableProps<WorkStationData>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Work Station",
      dataIndex: "name",
    },
    {
      title: "Address",
      dataIndex: "address",
      render: (val) => val || "-",
    },
    {
      title: "Charge-in Center",
      dataIndex: "companyChargeInCenter",
      render: (val) => val?.name || "-",
    },
    {
      title: "Status",
      dataIndex: "is_published",
      render: (val, record) => (
        <Space size={4}>
          <Switch
            className="custom-switch"
            checked={val === 1}
            onChange={() =>
              updateWorkStationStatus({
                id: record?.id,
                body: {
                  is_published: val === 0 ? 1 : 0,
                },
              })
            }
            disabled={isStatusUpdateLoading}
          />
          <span className="text-header text-sm font-medium">
            {WorkStationStatus[val]}
          </span>
        </Space>
      ),
    },
    // {
    //   title: "Total Employees",
    //   dataIndex: "employee_count",
    //   render: (val) => val ?? 0,
    // },
    {
      title: "Action",
      width: 100,
      render: (val) => (
        <Space size={8}>
          <Tooltip title="Edit Work Station">
            <Button
              className="action-button"
              size="small"
              icon={<EditIcon />}
              onClick={() =>
                handleOpen({
                  type: "UPDATE",
                  data: val?.id,
                })
              }
            />
          </Tooltip>
          <Popconfirm
            title="Delete work station"
            description="Are you sure you want to delete this work station?"
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true, loading: isDeleteLoading }}
            onConfirm={() => deleteWorkStation(val?.id)}
          >
            <Tooltip title="Delete Work Station">
              <Button
                danger
                className="action-button"
                size="small"
                icon={<DeleteIcon />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // success toast message
  useEffect(() => {
    if (isStatusUpdateSuccess) {
      toast("success", "Status updated successfully");
    }
  }, [isStatusUpdateSuccess]);

  useEffect(() => {
    if (isDeleteSuccess) {
      toast("success", "Work station deleted successfully");
    }
  }, [isDeleteSuccess]);

  return (
    <SidebarLayout title="Work Station List">
      <CustomTable
        loading={isLoading}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data?.totalLength}
        dataSource={addKeyInArray(data?.data?.workStations)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            <Input
              suffix={<SearchIcon />}
              placeholder="Search by name"
              onChange={(val) => {
                handleInputChange(val);
                resetPagination();
              }}
            />
            <Select
              allowClear
              showSearch
              placeholder="Status"
              filterOption={selectSearchOption}
              options={[
                { label: "Active", value: 1 },
                { label: "Inactive", value: 0 },
              ]}
              onChange={(value) => handleFilter("is_published", value)}
              className="min-w-[90px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
            />
            <Button
              icon={<AddIcon />}
              type="primary"
              className="sm-button"
              onClick={() =>
                handleOpen({
                  type: "ADD",
                })
              }
            >
              Add Work Station
            </Button>
          </>
        }
      />

      {(open.type === "ADD" || open.type === "UPDATE") && (
        <ManageWorkStation
          open={open.open}
          type={open.type}
          data={open.data}
          handleClose={handleClose}
        />
      )}
    </SidebarLayout>
  );
}
